/**
 * Bank accounts — `/finance/bank-accounts`.
 *
 * These are the company's OWN accounts at its own banks — where statements
 * arrive from. Supplier remittance instructions are a different fact about a
 * different counterparty and live under the supplier, not here.
 *
 * `code` and `currency` are immutable: statements already imported were filed
 * under both, and an account re-denominated after the fact is a different
 * account whose history would silently change meaning. The edit form shows
 * them read-only rather than sending a change the server would refuse.
 *
 * The general-ledger account and the in-transit clearing account must differ —
 * if they were the same, reconciling a line would be a journal that moves money
 * from an account to itself and proves nothing (`bank.validation_failed`).
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox } from "@erp/ui";
import { Plus } from "lucide-react";

import { DataTable, badgeCell } from "@/components/data-table";
import { localizedName } from "@/lib/i18n";
import { surfaceProblem } from "@/components/form";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  createBankAccount,
  replaceBankAccount,
  type BankAccount,
  type BankAccountWrite,
} from "../api";
import { PageHeader, ProblemBanner, SelectField, TextField } from "../components/fields";
import { financeKeys, useBankAccountList, usePostableAccounts } from "../queries";

const ALL = "__all__";
const NONE = "__none__";

interface AccountDraft {
  code: string;
  name: string;
  nameAr: string;
  iban: string;
  bic: string;
  accountNumber: string;
  currency: string;
  glAccountId: string;
  clearingAccountId: string;
  active: boolean;
}

const EMPTY_DRAFT: AccountDraft = {
  code: "",
  name: "",
  nameAr: "",
  iban: "",
  bic: "",
  accountNumber: "",
  currency: "SAR",
  glAccountId: NONE,
  clearingAccountId: NONE,
  active: true,
};

export interface BankAccountsPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
  canReadAccounts?: boolean;
  pageLimit?: number;
}

export function BankAccountsPage({
  canCreate = true,
  canUpdate = true,
  canReadAccounts = true,
  pageLimit,
}: BankAccountsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [activeFilter, setActiveFilter] = useState<string>(ALL);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<AccountDraft>(EMPTY_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);

  const filters = useMemo(
    () => (activeFilter === ALL ? {} : { active: activeFilter === "true" }),
    [activeFilter],
  );
  const listQuery = useBankAccountList(filters, pageLimit);
  const glAccountsQuery = usePostableAccounts(canReadAccounts);

  const accountOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      ...(glAccountsQuery.data ?? [])
        .filter((account) => account.is_postable)
        .map((account) => ({ value: account.id, label: `${account.code} · ${account.name}` })),
    ],
    [glAccountsQuery.data, t],
  );

  const resetForm = () => {
    setCreating(false);
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setFormError(null);
  };

  const startCreate = () => {
    setEditingId(null);
    setCreating(true);
    setDraft(EMPTY_DRAFT);
    setFormError(null);
  };

  const startEdit = (account: BankAccount) => {
    setCreating(false);
    setEditingId(account.id);
    setFormError(null);
    setDraft({
      code: account.code,
      name: account.name,
      nameAr: account.name_ar ?? "",
      iban: account.iban ?? "",
      bic: account.bic ?? "",
      accountNumber: account.account_number ?? "",
      currency: account.currency,
      glAccountId: account.gl_account_id,
      clearingAccountId: account.clearing_account_id,
      active: account.active,
    });
  };

  const createMutation = useIdempotentMutation<BankAccount, BankAccountWrite>({
    mutationFn: (body, key) => createBankAccount(body, key),
    onSuccess: (account) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.bankAccounts.all });
      startEdit(account);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { bankAccountId: string; body: BankAccountWrite }) =>
      replaceBankAccount(input.bankAccountId, input.body),
    onSuccess: (account) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.bankAccounts.all });
      startEdit(account);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const buildBody = (): BankAccountWrite | string => {
    if (draft.code.trim() === "" || draft.name.trim() === "") {
      return t("finance.bankAccounts.codeNameRequired", "A bank account needs a code and a name.");
    }
    if (draft.glAccountId === NONE || draft.clearingAccountId === NONE) {
      return t(
        "finance.bankAccounts.accountsRequired",
        "Choose both the general-ledger account and the in-transit clearing account.",
      );
    }
    if (draft.glAccountId === draft.clearingAccountId) {
      return t(
        "finance.bankAccounts.accountsMustDiffer",
        "The ledger account and the clearing account must differ — otherwise reconciling proves nothing.",
      );
    }
    return {
      code: draft.code.trim().toUpperCase(),
      name: draft.name.trim(),
      // The write is a full replace, so an empty box must send null rather
      // than be omitted — omitting it would keep the stored Arabic name.
      name_ar: draft.nameAr.trim() === "" ? null : draft.nameAr.trim(),
      currency: draft.currency.trim().toUpperCase(),
      gl_account_id: draft.glAccountId,
      clearing_account_id: draft.clearingAccountId,
      active: draft.active,
      ...(draft.iban.trim() === "" ? {} : { iban: draft.iban.trim().toUpperCase() }),
      ...(draft.bic.trim() === "" ? {} : { bic: draft.bic.trim().toUpperCase() }),
      ...(draft.accountNumber.trim() === "" ? {} : { account_number: draft.accountNumber.trim() }),
    };
  };

  const submit = () => {
    const body = buildBody();
    if (typeof body === "string") {
      setFormError(body);
      return;
    }
    setFormError(null);
    if (creating) createMutation.mutate(body);
    else if (editingId !== null) updateMutation.mutate({ bankAccountId: editingId, body });
  };

  const columns = useMemo<ColumnDef<BankAccount>[]>(
    () => [
      {
        accessorKey: "code",
        id: "code",
        header: t("finance.bankAccounts.columns.code", "Code"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "name",
        id: "name",
        header: t("finance.bankAccounts.columns.name", "Name"),
        enableSorting: false,
        cell: (ctx) =>
          localizedName(ctx.row.original.name, ctx.row.original.name_ar, i18n.language),
      },
      {
        accessorKey: "currency",
        id: "currency",
        header: t("finance.common.currency", "Currency"),
        enableSorting: false,
        cell: (ctx) => <span dir="ltr">{ctx.getValue<string>()}</span>,
      },
      {
        accessorKey: "iban",
        id: "iban",
        header: t("finance.bankAccounts.columns.iban", "IBAN"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono text-xs">
            {ctx.getValue<string>() ?? ""}
          </span>
        ),
      },
      {
        accessorKey: "active",
        id: "active",
        header: t("finance.bankAccounts.columns.active", "Active"),
        enableSorting: false,
        cell: (ctx) =>
          ctx.getValue<boolean>()
            ? badgeCell("true", { true: "success" }, t("finance.common.active", "Active"))
            : badgeCell("false", { false: "secondary" }, t("finance.common.inactive", "Inactive")),
      },
    ],
    [t, i18n.language],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.bankAccounts.title", "Bank accounts")}
        description={t(
          "finance.bankAccounts.subtitle",
          "The company's own accounts — where statements arrive from.",
        )}
        actions={
          canCreate ? (
            <Button type="button" onClick={startCreate}>
              <Plus className="size-4" aria-hidden="true" />
              {t("finance.bankAccounts.new", "New bank account")}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <SelectField
            id="bank-account-active"
            label={t("finance.bankAccounts.filterActive", "Status")}
            value={activeFilter}
            onValueChange={setActiveFilter}
            className="w-44"
            options={[
              { value: ALL, label: t("finance.common.all", "All") },
              { value: "true", label: t("finance.common.active", "Active") },
              { value: "false", label: t("finance.common.inactive", "Inactive") },
            ]}
          />
        </CardContent>
      </Card>

      <DataTable
        columns={columns}
        query={listQuery}
        getRowId={(row) => row.id}
        onRowClick={startEdit}
        storageKey="finance.bankAccounts.columns"
        emptyMessage={t("finance.bankAccounts.empty", "No bank accounts registered yet.")}
      />

      {creating || editingId !== null ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>
                {creating
                  ? t("finance.bankAccounts.createTitle", "New bank account")
                  : t("finance.bankAccounts.editTitle", "Edit {{code}}", { code: draft.code })}
              </h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <TextField
              id="bank-code"
              label={t("finance.bankAccounts.code", "Code")}
              value={draft.code}
              onChange={(code) => setDraft((current) => ({ ...current, code }))}
              disabled={!creating}
              hint={creating ? undefined : t("finance.bankAccounts.codeFrozen", "Immutable.")}
              ltr
            />
            <TextField
              id="bank-name"
              label={t("finance.bankAccounts.name", "Name")}
              value={draft.name}
              onChange={(name) => setDraft((current) => ({ ...current, name }))}
              disabled={!creating && !canUpdate}
              maxLength={140}
            />
            <TextField
              id="bank-name-ar"
              label={t("finance.bankAccounts.nameAr", "Name (Arabic)")}
              value={draft.nameAr}
              onChange={(nameAr) => setDraft((current) => ({ ...current, nameAr }))}
              disabled={!creating && !canUpdate}
              maxLength={255}
              hint={t(
                "finance.bankAccounts.nameArHint",
                "Optional. Shown to operators working in Arabic.",
              )}
            />
            <TextField
              id="bank-currency"
              label={t("finance.common.currency", "Currency")}
              value={draft.currency}
              onChange={(currency) =>
                setDraft((current) => ({ ...current, currency: currency.toUpperCase() }))
              }
              disabled={!creating}
              hint={creating ? undefined : t("finance.bankAccounts.currencyFrozen", "Immutable.")}
              ltr
              maxLength={3}
            />
            <TextField
              id="bank-iban"
              label={t("finance.bankAccounts.iban", "IBAN")}
              value={draft.iban}
              onChange={(iban) => setDraft((current) => ({ ...current, iban }))}
              disabled={!creating && !canUpdate}
              ltr
            />
            <TextField
              id="bank-bic"
              label={t("finance.bankAccounts.bic", "BIC")}
              value={draft.bic}
              onChange={(bic) => setDraft((current) => ({ ...current, bic }))}
              disabled={!creating && !canUpdate}
              ltr
            />
            <TextField
              id="bank-account-number"
              label={t("finance.bankAccounts.accountNumber", "Account number")}
              value={draft.accountNumber}
              onChange={(accountNumber) => setDraft((current) => ({ ...current, accountNumber }))}
              disabled={!creating && !canUpdate}
              ltr
              maxLength={64}
            />
            <SelectField
              id="bank-gl-account"
              label={t("finance.bankAccounts.glAccount", "Ledger account")}
              value={draft.glAccountId}
              onValueChange={(glAccountId) => setDraft((current) => ({ ...current, glAccountId }))}
              options={accountOptions}
              disabled={!canReadAccounts || (!creating && !canUpdate)}
              hint={t("finance.bankAccounts.glAccountHint", "Where the reconciled balance lives.")}
            />
            <SelectField
              id="bank-clearing-account"
              label={t("finance.bankAccounts.clearingAccount", "In-transit clearing account")}
              value={draft.clearingAccountId}
              onValueChange={(clearingAccountId) =>
                setDraft((current) => ({ ...current, clearingAccountId }))
              }
              options={accountOptions}
              disabled={!canReadAccounts || (!creating && !canUpdate)}
              hint={t(
                "finance.bankAccounts.clearingAccountHint",
                "Where money sits until the bank actually moves it.",
              )}
            />
            <label className="flex items-center gap-2 text-sm sm:col-span-2 lg:col-span-3">
              <Checkbox
                checked={draft.active}
                disabled={!creating && !canUpdate}
                onCheckedChange={(checked) =>
                  setDraft((current) => ({ ...current, active: checked === true }))
                }
              />
              {t("finance.bankAccounts.activeLabel", "Active")}
            </label>

            {formError === null ? null : (
              <div className="sm:col-span-2 lg:col-span-3">
                <ProblemBanner>{formError}</ProblemBanner>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 sm:col-span-2 lg:col-span-3">
              {creating || canUpdate ? (
                <Button
                  type="button"
                  loading={createMutation.isPending || updateMutation.isPending}
                  onClick={submit}
                >
                  {creating
                    ? t("finance.bankAccounts.create", "Register account")
                    : t("finance.bankAccounts.save", "Save account")}
                </Button>
              ) : null}
              <Button type="button" variant="ghost" onClick={resetForm}>
                {t("actions.cancel")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
